import CardActionButtonList from '../action-formatters/CardActionButtonList';
import { useMemo, useCallback } from 'react';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import { useDragDropComponentsContext } from '../internal/DragDropComponentsContext';
import { useCardContainerContext } from '../internal/CardContainerContext';
import Raven from 'raven-js';
import { ACTIONS_SELECTOR } from '../constants/CardSeleniumSelectors';
import { useCollapsedCardSuccessMarker } from '../internal/perf/cardPerfHooks';
import { useCardLocationContext } from '../internal/CardLocationContext';
import { CARD_FORMATTER_IFRAME_LOOKUP_KEYS } from '../constants/IframeLookupKeys';
import TruncatedTitle from '../components/TruncatedTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function CollapsibleCardFormatter({
  actionComponent,
  actionConfigs,
  cardId,
  cardType,
  children,
  isCollapsed = false,
  legacyDataAttributes: {
    ['data-sidebar-card-type']: legacyDataSidebarCardType,
    ['data-sidebar-card-association-object-type-id']: legacyDataSidebarCardAssociationObjectTypeId,
    ['data-sidebar-key']: legacyDataSidebarKey
  } = {},
  onCollapseCard,
  title,
  ActionFormatter = CardActionButtonList
}) {
  const {
    location,
    standaloneUsageTracker,
    additionalLocationData
  } = useCardLocationContext();
  const {
    SuccessMarker
  } = useCollapsedCardSuccessMarker();
  const dragDropComponents = useDragDropComponentsContext();
  const {
    reorderable
  } = useCardContainerContext();
  const DragHandle = useMemo(() => {
    if (reorderable && dragDropComponents != null && dragDropComponents.DragHandle != null) {
      return dragDropComponents.DragHandle;
    }
    if (reorderable) {
      Raven.captureMessage('CardStackContainerFormatter: no dragDropComponents passed in for location with reorderable=true, drag and drop functionality cannot be used');
    }
    return undefined;
  }, [dragDropComponents, reorderable]);
  const draggable = useMemo(() => {
    return reorderable && dragDropComponents != null;
  }, [dragDropComponents, reorderable]);
  const ToolbarActions = useMemo(() => {
    if (actionComponent) {
      return /*#__PURE__*/_jsx("div", {
        style: {
          width: 'max-content'
        },
        children: actionComponent
      });
    }
    if (actionConfigs) {
      return /*#__PURE__*/_jsx("div", {
        style: {
          width: 'max-content'
        },
        children: /*#__PURE__*/_jsx(ActionFormatter, {
          actionConfigs: actionConfigs
        })
      });
    }
    return undefined;
  }, [actionConfigs, actionComponent, ActionFormatter]);
  const onOpenChange = useCallback(e => {
    if (onCollapseCard) {
      const nextIsCollapsed = !e.target.value;
      onCollapseCard(cardId, nextIsCollapsed);
      standaloneUsageTracker === null || standaloneUsageTracker === void 0 || standaloneUsageTracker.track('crmCardInteraction', {
        action: nextIsCollapsed ? 'Card collapsed' : 'Card expanded',
        objectTypeId: additionalLocationData.objectTypeId,
        cardType,
        cardId,
        location
      });
    }
  }, [onCollapseCard, cardId, standaloneUsageTracker, additionalLocationData.objectTypeId, cardType, location]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isCollapsed && /*#__PURE__*/_jsx(SuccessMarker, {}), /*#__PURE__*/_jsx(ExpandableSection, {
      "data-sidebar-card-type": legacyDataSidebarCardType,
      "data-sidebar-card-association-object-type-id": legacyDataSidebarCardAssociationObjectTypeId,
      "data-sidebar-key": legacyDataSidebarKey,
      DragHandle: DragHandle,
      "data-selenium-expanded": !isCollapsed,
      "data-card-type": cardType,
      actions: ToolbarActions && /*#__PURE__*/_jsx("span", {
        "data-selenium-test": ACTIONS_SELECTOR,
        children: ToolbarActions
      }),
      titleText: /*#__PURE__*/_jsx(TruncatedTitle, {
        title: title
      }),
      indentation: "compact",
      draggable: draggable,
      defaultOpen: !isCollapsed,
      onOpenChange: onOpenChange,
      children: children
    })]
  });
}
CollapsibleCardFormatter.iframeLookupKey = CARD_FORMATTER_IFRAME_LOOKUP_KEYS.COLLAPSIBLE_CARD_FORMATTER;